import { filter, find, findIndex, forEach, get, isEmpty, map, omit, round, some } from 'lodash-es';
import uuidv4 from 'uuid/v4';

import { buildServicesData } from './utils';

const getColumns = (contracts, parties, isLoan) => {
  const columns = [];

  if (isLoan) {
    columns.push({ id: uuidv4(), label: '', type: 'string' });
  } else {
    columns.push({ id: `Garanties${uuidv4()}`, label: 'quotation.offer.coverages', type: 'string' });
  }

  forEach(contracts, contract => {
    forEach(get(contract, 'offer', []), offer => {
      forEach(get(offer, 'covereds', []), cov => {
        const party = find(parties, { ref: get(cov, 'party.ref') });
        const fullName = `${party.name} ${party.first_name}`;
        columns.push({ id: `${fullName}${uuidv4()}`, label: fullName, type: 'currency', ref: get(cov, 'party.ref') });
      });
    });
  });

  if (isLoan) {
    columns.push({ id: `Frais${uuidv4()}`, label: 'quotation.offer.fees', type: 'currency' });
  }

  columns.push({ id: `Total${uuidv4()}`, label: 'quotation.offer.total', type: 'currency' });

  return columns;
};

const getLinesName = (contracts, isLoan) => {
  if (isLoan) {
    return [
      { label: 'quotation.offer.premium_sum', code: 'premium_sum' },
      { label: 'quotation.offer.premium_mean', code: 'premium_mean' },
      { label: 'quotation.offer.premium_first_year_sum', code: 'premium_first_year_sum' },
      { label: 'quotation.offer.premium_taea', code: 'premium_taea' },
    ];
  } else {
    const linesName = [];

    forEach(contracts, contract => {
      forEach(get(contract, 'offer', []), offer => {
        forEach(get(offer, 'covereds', []), cov => {
          forEach(get(cov, 'coverages', []), coverage => {
            if (!some(linesName, { code: coverage?.coverage?.code })) {
              linesName.push({ label: coverage?.coverage?.name, code: coverage?.coverage?.code });
            }
          });
        });
      });
    });

    linesName.push({ label: 'quotation.offer.fees', code: 'fees' });
    linesName.push({ label: 'quotation.offer.total', code: 'total' });

    return linesName;
  }
};

/**
 * Get total of all years
 * @param {object} contracts Contracts
 * @param {number} column Column
 * @param {object} columnTypes Type of column between isCoveredColumn, isFeeColumn, isLastColumn
 */
const getPremiumSum = (contracts, column, columnTypes) => {
  let premiumSum = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      if (columnTypes.isCoveredColumn) {
        const cov = find(offer.covereds, { party: { ref: column.ref } });

        if (cov) {
          premiumSum += parseFloat(get(cov, `premium.total_premium`, 0)) + parseFloat(get(cov, `premium.total_tax`, 0));
        }
      }

      if (columnTypes.isFeeColumn) premiumSum += parseFloat(get(offer, 'premium.total_fee', 0));

      if (columnTypes.isLastColumn) premiumSum += parseFloat(get(offer, 'premium.total', 0));
    });
  });

  return premiumSum;
};

/**
 * Get mean of all years
 * @param {object} contracts Contracts
 * @param {number} column Column
 * @param {object} columnTypes Type of column between isCoveredColumn, isFeeColumn, isLastColumn
 */
const getPremiumMean = (contracts, column, columnTypes) => {
  let premiumMean = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      if (columnTypes.isCoveredColumn) {
        const cov = find(offer.covereds, { party: { ref: column.ref } });

        if (cov) {
          premiumMean +=
            parseFloat(get(cov, `premium.average_total_premium`, 0)) +
            parseFloat(get(cov, `premium.average_total_tax`, 0));
        }
      }

      if (columnTypes.isFeeColumn) premiumMean += parseFloat(get(offer, 'premium.average_total_fee', 0));

      if (columnTypes.isLastColumn) premiumMean += parseFloat(get(offer, 'premium.average_total', 0));
    });
  });

  return premiumMean;
};

/**
 * Get total of first year
 * @param {object} contracts Contracts
 * @param {number} column Column
 * @param {object} columnTypes Type of column between isCoveredColumn, isFeeColumn, isLastColumn
 */
const getFirstYearSum = (contracts, column, columnTypes) => {
  let firstYearSum = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      if (columnTypes.isCoveredColumn) {
        const cov = find(offer.covereds, { party: { ref: column.ref } });

        if (cov) {
          firstYearSum +=
            parseFloat(get(offer, 'premium.first_year_premium', 0)) +
            parseFloat(get(offer, 'premium.first_year_tax', 0));
        }
      }

      if (columnTypes.isFeeColumn) firstYearSum += parseFloat(get(offer, 'premium.first_year_fee', 0));

      if (columnTypes.isLastColumn) firstYearSum += parseFloat(get(offer, 'premium.first_year_total', 0));
    });
  });

  return firstYearSum;
};

/**
 * Get TAEA
 * @param {object} contracts Contracts
 * @param {number} column Column
 * @param {object} columnTypes Type of column between isCoveredColumn, isFeeColumn, isLastColumn
 */
const getTaea = (contracts, column, columnTypes) => {
  let taea = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      if (columnTypes.isCoveredColumn) {
        const cov = find(offer.covereds, { party: { ref: column.ref } });

        if (cov) {
          taea = { value: parseFloat(get(offer, 'taea[0].taea', 0)), format: 'percent' };
        }
      }
    });
  });

  return taea;
};

/**
 * Get Fees from first year
 * @param {object} contracts Contracts
 * @param {object} columnTypes Type of column between isCoveredColumn, isLastColumn
 */
const getFees = (contracts, columnTypes) => {
  let fees = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      if (columnTypes.isLastColumn) {
        fees += parseFloat(get(offer, 'schedule[0].fee', 0));
      }
    });
  });

  return fees;
};

/**
 * Get Total from first year
 * @param {object} contracts Contracts
 * @param {object} columnTypes Type of column between isCoveredColumn, isLastColumn
 */
const getTotal = (contracts, columnTypes) => {
  let total = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      if (columnTypes.isLastColumn) {
        total += parseFloat(get(offer, 'schedule[0].total', 0));
      }
    });
  });

  return total;
};

/**
 * Get Detail Coverage from first year
 * @param {object} contracts Contracts
 * @param {number} column Column
 * @param {object} columnTypes Type of column between isCoveredColumn, isLastColumn
 * @param {object} code Code of coverage to get (coverage code)
 */
const getDetailCoverage = (contracts, column, columnTypes, code) => {
  let detailSum = 0;

  forEach(contracts, contract => {
    forEach(contract.offer, offer => {
      forEach(get(offer, 'schedule[0].details', []), detail => {
        if (get(detail, 'origin.option.coverage.code') === code) {
          if (columnTypes.isCoveredColumn) {
            if (get(detail, 'origin.covered.party.ref') === column.ref) {
              detailSum += parseFloat(get(detail, 'total', 0));
            }
          }

          if (columnTypes.isLastColumn) {
            detailSum += parseFloat(get(detail, 'total', 0));
          }
        }
      });
    });
  });

  return detailSum;
};

/**
 * Replace loan ref to array index
 * @param {Array} list Array of objects that have loan key with ref
 * @param {Array} loans SR loans
 * @returns
 */
const replaceLoanRef = (list, loans) => {
  if (list) {
    return map(list, obj => {
      if (obj?.loan?.ref && loans) {
        const idx = findIndex(loans, { ref: obj.loan.ref });

        if (idx >= 0) {
          return { ...obj, loan: { ...obj.loan, ref: `${idx}` } };
        }
      }

      return obj;
    });
  }
};

/**
 * Replace party ref to array index
 * @param {Array} list Array of objects that have party key with ref
 * @param {Array} idx Contract idx (so quotation's covered index...)
 * @returns
 */
const replacePartyRef = (list, idx) => {
  if (list) {
    return map(list, obj => {
      if (obj?.origin?.covered?.party?.ref) {
        return {
          ...obj,
          origin: {
            ...obj.origin,
            covered: {
              ...obj.origin.covered,
              party: { ...obj.origin.covered.party, ref: `${idx}`, _oldRef: obj.origin.covered.party.ref },
            },
          },
        };
      }

      return obj;
    });
  }
};

/**
 * Parse TAEA to Float
 * @param {Array} list Array of objects that have taea key
 * @returns
 */
const parseTaea = list => map(list, obj => ({ ...obj, taea: parseFloat(obj.taea) }));

/**
 * Round data (all_contract, first, first_year and mean)
 * @param {object} data Object with value to round
 * @param {number} precision Precision to round
 * @param {array} fields List of fields to round
 * @param {boolean} stringify If data if stringified
 * @returns
 */
const roundData = ({
  data,
  precision = 2,
  fields = ['all_contract', 'first', 'first_year', 'mean'],
  stringify = false,
}) => {
  const roundedData = { ...data };

  forEach(fields, field => {
    if (roundedData[field] != null) {
      roundedData[field] = round(roundedData[field], precision);

      if (stringify) {
        roundedData[field] = `${roundedData[field].toFixed(precision)}`;
      }
    }
  });

  return roundedData;
};

const PREMIUM_SUMMARY = {
  all_contract: 0,
  first: 0,
  first_year: 0,
  mean: 0,
};
/**
 * getOfferFromV2 - extract offer data from API v2 format 
 * and transform them into api v1 format
 * @param {*} contracts 
 * @param {*} parties 
 * @param {*} loans 
 * @returns offer
 */
export const getOfferFromV2 = (contracts, parties, loans) => {
  let schedules = [];

  // Replace loan ref for all schedules
  forEach(contracts, (contract, idx) => {
    forEach(get(contract, 'offer', []), offer => {
      schedules.push(
        map(offer.schedule, sched => {
          return { ...sched, details: replacePartyRef(replaceLoanRef(sched.details, loans), idx) };
        })
      );
    });
  });

  const by_covered = [];
  const contractWholePrice = { total: 0, total_fee: 0, total_premium: 0, total_tax: 0 };
  let cptIdx = 0;
  let isLoan = false;

  // Build premium_summary
  forEach(contracts, (contract, idxContract) => {
    forEach(contract.offer, (offer, idxOffer) => {
      const newCov = {
        all_coverages: { ...PREMIUM_SUMMARY },
        by_coverage: {},
        whole_price: { ...PREMIUM_SUMMARY },
      };
      let byLoan = [];
      cptIdx = idxContract + idxOffer;

      // Get data by loan and by coverage
      forEach(get(schedules, `[${cptIdx}]`, []), (schedule, idx) => {
        let addedTax = false;

        if (schedule.details) {
          forEach(schedule.details, detail => {
            if (!detail?.origin?.fee) {
              let loanIdx = 0;

              if (detail.loan) {
                loanIdx = parseInt(detail.loan.ref, 10);
                isLoan = true;
              }

              if (!byLoan[loanIdx]) {
                byLoan[loanIdx] = {};
              }

              if (detail?.origin?.covered?.party?.ref && isEmpty(byLoan[loanIdx])) {
                const covered = find(offer.covereds, { party: { ref: detail.origin.covered.party._oldRef } });

                forEach(get(covered, 'coverages', []), coverage => {
                  byLoan[loanIdx][coverage.coverage.code] = {
                    ...PREMIUM_SUMMARY,
                    eligibility: coverage.eligibility,
                    _cptMean: 0,
                    fee: { ...PREMIUM_SUMMARY, _cptMean: 0 },
                    premium: 0,
                    tax: 0,
                  };
                });
              }

              if (detail?.origin?.option?.coverage?.code) {
                const covCode = detail.origin.option.coverage.code;

                if (!idx) {
                  byLoan[loanIdx][covCode].first = parseFloat(detail.premium) + parseFloat(detail.tax);
                  byLoan[loanIdx][covCode].first_year = parseFloat(detail.premium) + parseFloat(detail.tax);
                }

                byLoan[loanIdx][covCode].premium += parseFloat(detail.premium);
                byLoan[loanIdx][covCode].all_contract += parseFloat(detail.premium) + parseFloat(detail.tax);
                byLoan[loanIdx][covCode].mean += parseFloat(detail.premium) + parseFloat(detail.tax);

                if (!isLoan && !addedTax) {
                  byLoan[loanIdx][covCode].tax += parseFloat(schedule.tax);
                  addedTax = true;
                }
              }
            }
          });
        }

        byLoan = map(byLoan, loan => {
          forEach(loan, (coverage, covCode) => {
            loan[covCode] = {
              ...coverage,
              _cptMean: coverage._cptMean + 1,
              fee: {
                all_contract: coverage.fee.all_contract + parseFloat(schedule.fee),
                first: !idx ? parseFloat(schedule.fee) : coverage.fee.first,
                first_year: !idx ? parseFloat(schedule.fee) : coverage.fee.first_year,
                mean: coverage.fee.mean + parseFloat(schedule.fee),
                _cptMean: coverage.fee._cptMean + 1,
              },
            };
          });

          return loan;
        });
      });

      if (isLoan) {
        newCov.by_loan = [];
        newCov.taea = parseTaea(replaceLoanRef(offer.taea, loans));
      }

      forEach(byLoan, (loan, idx) => {
        const objLoan = {
          all_coverages: { ...PREMIUM_SUMMARY },
          by_coverage: {},
          taea: filter(newCov.taea, taea => taea.loan.ref === `${idx}`),
          whole_price: { ...PREMIUM_SUMMARY },
        };

        forEach(loan, (coverage, covCode) => {
          if (isLoan) {
            // by_loan
            if (!objLoan.by_coverage[covCode]) {
              objLoan.by_coverage[covCode] = omit(coverage, ['eligibility', 'fee', 'premium', 'tax']);
            }

            // by_loan all_coverages
            objLoan.all_coverages.all_contract += coverage.all_contract;
            objLoan.all_coverages.first += coverage.first;
            objLoan.all_coverages.first_year += coverage.first_year;
            objLoan.all_coverages.mean += coverage.mean;
            if (!objLoan.all_coverages._cptMean) {
              objLoan.all_coverages._cptMean = coverage._cptMean;
            }

            // by_loan whole_price
            if (!objLoan.whole_price._addedFee) {
              objLoan.whole_price.all_contract += coverage.fee.all_contract;
              objLoan.whole_price.first += coverage.fee.first;
              objLoan.whole_price.first_year += coverage.fee.first_year;
              objLoan.whole_price.mean += coverage.fee.mean;
              if (!objLoan.whole_price._cptMean) {
                objLoan.whole_price._cptMean = coverage._cptMean;
              }

              objLoan.whole_price._addedFee = true;
            }
          }

          // by_coverage
          if (!newCov.by_coverage[covCode]) {
            newCov.by_coverage[covCode] = omit(coverage, ['fee', 'premium', 'tax']);
          } else {
            newCov.by_coverage[covCode].all_contract += coverage.all_contract;
            newCov.by_coverage[covCode].first += coverage.first;
            newCov.by_coverage[covCode].first_year += coverage.first_year;
            newCov.by_coverage[covCode].mean += coverage.mean;
          }

          // by_coverage all_coverages
          newCov.all_coverages.all_contract += coverage.all_contract;
          newCov.all_coverages.first += coverage.first;
          newCov.all_coverages.first_year += coverage.first_year;
          newCov.all_coverages.mean += coverage.mean;
          if (!newCov.all_coverages._cptMean) {
            newCov.all_coverages._cptMean = coverage._cptMean;
          }

          // by_coverage whole_price
          if (!newCov.whole_price._addedFee) {
            newCov.whole_price.all_contract += coverage.fee.all_contract;
            newCov.whole_price.first += coverage.fee.first;
            newCov.whole_price.first_year += coverage.fee.first_year;
            newCov.whole_price.mean += coverage.fee.mean;
            if (!newCov.whole_price._cptMean) {
              newCov.whole_price._cptMean = coverage.fee._cptMean;
            }

            if (!isLoan) {
              contractWholePrice.total += coverage.fee.all_contract;
              contractWholePrice.total_fee += coverage.fee.all_contract;
            }

            newCov.whole_price._addedFee = true;
          }

          if (!isLoan) {
            contractWholePrice.total += coverage.all_contract;
            contractWholePrice.total_premium += coverage.premium;

            if (!newCov.whole_price._addedTax) {
              contractWholePrice.total += coverage.tax;
              contractWholePrice.total_tax += coverage.tax;
            }
          }
        });

        if (isLoan) {
          newCov.by_loan.push(objLoan);
        }
      });

      // by_coverage mean and round
      forEach(newCov.by_coverage, (coverage, covCode) => {
        if (coverage.mean) {
          coverage.mean /= coverage._cptMean;
        }
        newCov.by_coverage[covCode] = roundData({ data: omit(coverage, ['_cptMean']) });
      });

      // by_loan mean, round and whole_price
      if (isLoan) {
        forEach(newCov.by_loan, (loan, idx) => {
          // all_coverages
          if (loan.all_coverages.mean) {
            loan.all_coverages.mean /= loan.all_coverages._cptMean;
          }
          newCov.by_loan[idx].all_coverages = roundData({ data: omit(loan.all_coverages, ['_cptMean']) });

          // by_coverage
          forEach(loan.by_coverage, (coverage, covCode) => {
            if (coverage.mean) {
              coverage.mean /= coverage._cptMean;
            }
            newCov.by_loan[idx].by_coverage[covCode] = roundData({ data: omit(coverage, ['_cptMean']) });
          });

          // whole_price
          loan.whole_price.all_contract += loan.all_coverages.all_contract;
          loan.whole_price.first += loan.all_coverages.first;
          loan.whole_price.first_year += loan.all_coverages.first_year;
          if (loan.whole_price.mean) {
            loan.whole_price.mean /= loan.whole_price._cptMean;
          }
          loan.whole_price.mean += newCov.by_loan[idx].all_coverages.mean;
          newCov.by_loan[idx].whole_price = roundData({
            data: omit(loan.whole_price, ['_cptMean', '_addedFee', '_addedTax']),
          });
        });
      }

      // all_coverages mean and round
      if (newCov.all_coverages.mean) {
        newCov.all_coverages.mean /= newCov.all_coverages._cptMean;
      }
      newCov.all_coverages = roundData({ data: omit(newCov.all_coverages, ['_cptMean']) });

      // whole_price sum, mean and round
      newCov.whole_price.all_contract += newCov.all_coverages.all_contract;
      newCov.whole_price.first += newCov.all_coverages.first;
      newCov.whole_price.first_year += newCov.all_coverages.first_year;
      if (newCov.whole_price.mean) {
        newCov.whole_price.mean /= newCov.whole_price._cptMean;
      }
      newCov.whole_price.mean += newCov.all_coverages.mean;
      newCov.whole_price = roundData({ data: omit(newCov.whole_price, ['_cptMean', '_addedFee', '_addedTax']) });

      by_covered.push(newCov);
    });
  });

  // Remove _oldRef
  schedules = map(schedules, sched => {
    return map(sched, subSched => {
      return {
        ...subSched,
        details: map(get(subSched, 'details', []), detail => {
          if (detail?.origin?.covered?.party) {
            return {
              ...detail,
              origin: {
                ...detail.origin,
                covered: {
                  ...detail.origin.covered,
                  party: omit(detail.origin.covered.party, ['_oldRef']),
                },
              },
            };
          }

          return detail;
        }),
      };
    });
  });

  const premium_summary = { by_covered };

  if (isLoan && loans && loans[0]) {
    premium_summary.fee_holding_loan_ref = '0';
  }

  // Add contract_first_invoice and contract_whole_price
  if (!isLoan) {
    premium_summary.contract = {
      contract_first_invoice: {
        total: get(schedules, `[0][0].total`, 0),
        total_fee: get(schedules, `[0][0].fee`, 0),
        total_premium: get(schedules, `[0][0].premium`, 0),
        total_tax: get(schedules, `[0][0].tax`, 0),
      },
      contract_whole_price: roundData({
        data: contractWholePrice,
        fields: ['total', 'total_fee', 'total_premium', 'total_tax'],
        stringify: true,
      }),
    };
  }

  const columns = getColumns(contracts, parties, isLoan);
  const columnsLength = columns.length;
  const linesName = getLinesName(contracts, isLoan);
  const data = [];

  linesName.forEach(({ label, code }) => {
    const line = { id: `${code}${uuidv4()}` };

    columns.forEach((column, idx) => {
      if (idx === 0) {
        line[column.id] = label;
        return;
      }

      let val;
      let columnTypes = {};

      if (isLoan) {
        columnTypes = {
          isCoveredColumn: idx > 0 && idx < columnsLength - 2,
          isFeeColumn: idx === columnsLength - 2,
          isLastColumn: idx === columnsLength - 1,
        };
      } else {
        columnTypes = {
          isCoveredColumn: idx > 0 && idx < columnsLength - 1,
          isLastColumn: idx === columnsLength - 1,
        };
      }

      switch (code) {
        case 'premium_sum':
          val = getPremiumSum(contracts, column, columnTypes);
          break;
        case 'premium_mean':
          val = getPremiumMean(contracts, column, columnTypes);
          break;
        case 'premium_first_year_sum':
          val = getFirstYearSum(contracts, column, columnTypes);
          break;
        case 'premium_taea':
          val = getTaea(contracts, column, columnTypes);
          break;
        case 'fees':
          val = getFees(contracts, columnTypes);
          break;
        case 'total':
          val = getTotal(contracts, columnTypes);
          break;
        default:
          val = getDetailCoverage(contracts, column, columnTypes, code);
          break;
      }

      if (val != null) line[column.id] = val;
    });

    data.push(line);
  });

  // dirty hack
  const formalities = contracts[0]?.offer[0]?.grouped_document_requests;

  // find services 'ij_amount'
  const { services, extra_details } = buildServicesData(contracts[0]?.offer[0]) || {};

  return { columns, data, schedules, premium_summary, formalities, services, extra_details };
};
